import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { path } from '~/app/navigation';
import { useTranslation } from 'react-i18next';
import { Code, Stamp } from '~/design-system';
import { runSearch, type ResolvedRegistry } from '~/platform/registry/registry';
import type { SearchHit } from '~/platform/registry/types';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import './CommandPalette.css';

/* ------------------------------------------------------------------ *
 * One search field for the whole product.
 *
 * Results come from the module registry, so a module that ships next
 * year is searchable the day it is registered without touching this
 * file. Every hit renders as a miniature crate label — band, code,
 * name, stamp — which is the same anatomy the tables use, so the
 * palette teaches the visual system rather than being an exception to
 * it.
 * ------------------------------------------------------------------ */

export function CommandPalette({
  open,
  onOpenChange,
  registry,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  registry: ResolvedRegistry;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { business } = useBusiness();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [cursor, setCursor] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands = useMemo(() => {
    const q = query.trim().toLowerCase();
    return registry.commands.filter((c) =>
      q ? t(c.labelKey).toLowerCase().includes(q) : true,
    );
  }, [registry.commands, query, t]);

  const rows = useMemo(
    () => [
      ...hits.map((h) => ({ kind: 'hit' as const, hit: h })),
      ...commands.map((c) => ({ kind: 'command' as const, command: c })),
    ],
    [hits, commands],
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      setHits([]);
      setCursor(0);
      return;
    }
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setBusy(true);
    const timer = setTimeout(async () => {
      const found = await runSearch(registry, query, business.id, business.slug);
      if (!cancelled) {
        setHits(found.slice(0, 20));
        setCursor(0);
        setBusy(false);
      }
    }, 160);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open, registry, business.id]);

  const run = (index: number) => {
    const row = rows[index];
    if (!row) return;
    if (row.kind === 'hit') {
      void navigate({ to: path(row.hit.to) });
    } else {
      void row.command.run({
        businessSlug: business.slug,
        navigate: (to) => void navigate({ to: path(to) }),
      });
    }
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <div
      className="pd-palette__scrim"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div
        className="pd-palette"
        role="dialog"
        aria-modal="true"
        aria-label={t('search.openPalette')}
      >
        <div className="pd-palette__input-row">
          <input
            ref={inputRef}
            className="pd-palette__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('search.placeholder')}
            autoComplete="off"
            spellCheck={false}
            role="combobox"
            aria-expanded="true"
            aria-controls="pd-palette-list"
            aria-activedescendant={`pd-palette-row-${cursor}`}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(rows.length - 1, c + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(0, c - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                run(cursor);
              } else if (e.key === 'Escape') {
                onOpenChange(false);
              }
            }}
          />
          {busy ? <span className="pd-palette__busy" aria-hidden="true" /> : null}
          <kbd className="num pd-palette__esc">ESC</kbd>
        </div>

        <ul className="pd-palette__list scroll-y" id="pd-palette-list" role="listbox">
          {rows.length === 0 ? (
            <li className="pd-palette__note">
              {query.trim().length < 2 ? t('search.hint') : t('search.noResults', { query })}
            </li>
          ) : null}

          {rows.map((row, index) => (
            <li
              key={row.kind === 'hit' ? row.hit.id : row.command.id}
              id={`pd-palette-row-${index}`}
              role="option"
              aria-selected={index === cursor}
              className="pd-palette__row"
              data-active={index === cursor || undefined}
              data-hc={row.kind === 'hit' ? row.hit.handlingClass ?? 'neutral' : 'neutral'}
              onPointerEnter={() => setCursor(index)}
              onClick={() => run(index)}
            >
              <span className="pd-band" aria-hidden="true" />
              {row.kind === 'hit' ? (
                <>
                  {row.hit.code ? <Code>{row.hit.code}</Code> : null}
                  <span className="pd-palette__title truncate">{row.hit.title}</span>
                  {row.hit.subtitle ? (
                    <span className="pd-palette__sub truncate">{row.hit.subtitle}</span>
                  ) : null}
                  {row.hit.stamp}
                </>
              ) : (
                <>
                  <Stamp tone="accent" size="sm">
                    {t('search.commands')}
                  </Stamp>
                  <span className="pd-palette__title truncate">{t(row.command.labelKey)}</span>
                  {row.command.shortcut ? (
                    <kbd className="num pd-palette__shortcut">{row.command.shortcut}</kbd>
                  ) : null}
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Ctrl/Cmd-K anywhere. Registered once by the shell. */
export const usePaletteHotkey = (onOpen: () => void): void => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpen]);
};
